import React from 'react';
import theme from '../../styles/theme';

/**
 * Reusable button component with consistent styling
 * 
 * @param {string} variant - Button style variant: 'primary', 'secondary', 'accent', 'outline', 'danger'
 * @param {string} size - Button size: 'sm', 'md', 'lg'
 * @param {React.ReactNode} icon - Optional icon element
 * @param {React.ReactNode} children - Button text/content
 * @param {function} onClick - Click handler
 * @param {boolean} disabled - Disabled state
 * @param {object} style - Additional custom styles
 */
const Button = ({ 
  variant = 'primary', 
  size = 'md', 
  icon, 
  children, 
  onClick, 
  disabled = false,
  style = {},
  ...props 
}) => {
  const sizeStyles = {
    sm: {
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      fontSize: theme.typography.fontSize.sm,
      minHeight: '40px',
    },
    md: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSize.base,
      minHeight: '44px',
    },
    lg: {
      padding: `${theme.spacing.md} ${theme.spacing.xl}`,
      fontSize: theme.typography.fontSize.base,
      minHeight: '48px',
    },
  };

  const variantStyles = {
    primary: {
      backgroundColor: theme.colors.primary.main,
      color: theme.colors.primary.contrast,
      border: 'none',
    },
    secondary: {
      backgroundColor: theme.colors.neutral[100],
      color: theme.colors.text.primary,
      border: `1px solid ${theme.colors.border.light}`,
    },
    accent: {
      backgroundColor: theme.colors.accent.main,
      color: theme.colors.accent.contrast,
      border: 'none',
    },
    outline: {
      backgroundColor: 'transparent',
      color: theme.colors.text.secondary,
      border: `1px solid ${theme.colors.border.light}`,
    },
    danger: {
      backgroundColor: theme.colors.error,
      color: theme.colors.primary.contrast,
      border: 'none',
    },
  };

  const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    fontWeight: theme.typography.fontWeight.medium,
    borderRadius: theme.borderRadius.md,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: theme.transitions.fast,
    opacity: disabled ? 0.5 : 1,
    whiteSpace: 'nowrap',
    boxShadow: variant === 'primary' || variant === 'accent' ? theme.shadows.sm : 'none',
    ...variantStyles[variant],
    ...sizeStyles[size],
    ...style,
  };

  return (
    <button
      style={baseStyles}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.target.style.opacity = '0.9';
          if (variant === 'outline' || variant === 'secondary') {
            e.target.style.backgroundColor = theme.colors.neutral[100];
          }
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.target.style.opacity = '1';
          if (variant === 'outline') {
            e.target.style.backgroundColor = 'transparent';
          }
          if (variant === 'secondary') {
            e.target.style.backgroundColor = theme.colors.neutral[100];
          }
        }
      }}
      {...props}
    >
      {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
      {children}
    </button>
  );
};

export default Button;
